import EmployeeSearchPicker from '../components/EmployeeSearchPicker';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Container, Row, Col, Card, Table, Form, Button, Alert, Spinner, Pagination, Badge, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import ImageWithDetectionBox from '../components/ImageWithDetectionBox';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { ppeAPI, departmentAPI, faceImageAPI, isRequestCanceled } from '../utils/api';
import { useListRequest } from '../hooks/useListRequest';
import type { PPERow, PPEFilters } from '../types/ppe';
import type { Department } from '../types/department';
import { getTodayDateInCompanyTimezone, formatDateTimeInCompanyTimezone } from '../utils/timezone';
import { resolvePpeImagePathSegments } from '../utils/ppeImagePath';

import ScopeIndicator from '../components/ScopeIndicator';

interface PPEDetectionCardProps {
  row: PPERow;
  thumbUrl: string | null;
  companyTimezone: string;
  activePpeFields: Array<{ key: string; label: string; confKey?: string }>;
  onOpen: (row: PPERow) => void;
  renderCompliance: (value: number | boolean | undefined, confidence?: number) => React.ReactNode;
  t: ReturnType<typeof useTranslation>['t'];
  tc: ReturnType<typeof useTranslation>['t'];
}

const PPEDetectionCard: React.FC<PPEDetectionCardProps> = ({
  row,
  thumbUrl,
  companyTimezone,
  activePpeFields,
  onOpen,
  renderCompliance,
  t,
  tc,
}) => (
  <Card className="an-data-card border-0 rounded-4 mb-3 w-100" onClick={() => onOpen(row)} style={{ cursor: 'pointer', maxWidth: '100%' }}>
    <Card.Body className="p-3">
      <div className="d-flex flex-column flex-sm-row gap-3 align-items-start">
        <div className="flex-shrink-0 mx-auto mx-sm-0">
          {thumbUrl ? (
            <img
              src={thumbUrl}
              alt={t('title')}
              className="rounded shadow-sm"
              style={{ width: '68px', height: '68px', objectFit: 'cover' }}
            />
          ) : (
            <div className="text-muted" style={{ width: '68px', height: '68px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--an-border)', borderRadius: 'var(--an-radius-sm)' }}>
              {tc('messages.na')}
            </div>
          )}
        </div>

        <div className="flex-grow-1 min-w-0 w-100">
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start gap-2 mb-2">
            <div className="min-w-0">
              <div className="fw-bold">{row.employee_name || t('unknown')}</div>
              <small className="text-muted">ID: {row.emp_id || tc('messages.na')}</small>
            </div>
            <small className="text-muted text-sm-end">{formatDateTimeInCompanyTimezone(row.log_time, companyTimezone)}</small>
          </div>

          <div className="small text-muted mb-2">
            <div>{tc('labels.department')}: {row.department_name || t('unknown')}</div>
            <div>{tc('labels.camera')}: {row.camera_name || t('unknown')}</div>
          </div>

          <div className="d-flex flex-wrap gap-1 mb-2">
            {row.is_vacation && <Badge bg="info">{t('vacation')}</Badge>}
            {row.is_holiday && <Badge bg="success">{t('holiday')}</Badge>}
          </div>

          <div className="d-flex flex-wrap gap-2">
            {activePpeFields.map(field => (
              <div key={field.key} className="d-flex flex-column align-items-center p-2 an-panel-soft" style={{ minWidth: '72px', maxWidth: '100%' }}>
                <small className="text-muted mb-1 text-center" style={{ fontSize: '0.65rem' }}>{field.label}</small>
                {renderCompliance(row[field.key], field.confKey ? row[field.confKey] : undefined)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card.Body>
  </Card>
);

const PPEDetections: React.FC = () => {
  const { currentCompany, userRolePermissionsLoading } = useAuth();
  const { canViewPPE } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'ppeDetections' });
  const { t: tc } = useTranslation('common');
  const companyTimezone = currentCompany?.timezone || 'UTC';

  const [detections, setDetections] = useState<PPERow[]>([]);
  const [ppeTypes, setPpeTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPPEDisabled, setIsPPEDisabled] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedDetection, setSelectedDetection] = useState<PPERow | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const [meta, setMeta] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1
  });

  const [filters, setFilters] = useState<PPEFilters>({
    date_from: '',
    date_to: '',
    per_page: 20,
    page: 1
  });

  const [formState, setFormState] = useState({
    date_from: '',
    date_to: '',
    employee_id: '',
    department_id: ''
  });

  const [departments, setDepartments] = useState<Department[]>([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  const currentCompanyRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);

  // Each load owns its signal; a newer filter/company load or unmount cancels the superseded request.
  const { begin } = useListRequest(String(currentCompany?.company_id));

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadDetections = useCallback(async () => {
    if (!currentCompany || !canViewPPE) return;

    const requestCompanyId = currentCompany.company_id;
    currentCompanyRef.current = requestCompanyId;
    const controller = begin();

    try {
      if (!isMountedRef.current) return;

      setLoading(true);
      setError(null);
      setIsPPEDisabled(false);

      const response = await ppeAPI.getPPEDetections(requestCompanyId, filters, controller.signal);
      
      if (controller.signal.aborted) return;
      if (isMountedRef.current && currentCompanyRef.current === requestCompanyId) {
        setDetections(response.data || []);
        setPpeTypes(response.ppe_types || []);
        setMeta({
          current_page: response.meta?.current_page ?? 1,
          per_page: response.meta?.per_page ?? 20,
          total: response.meta?.total ?? 0,
          last_page: Math.max(1, response.meta?.last_page ?? 1)
        });
      }
    } catch (err: any) {
      if (controller.signal.aborted || isRequestCanceled(err)) return;
      if (isMountedRef.current && currentCompanyRef.current === requestCompanyId) {
        console.error('Error loading PPE detections:', err);
        if (err.response?.status === 403) {
          setIsPPEDisabled(true);
          setError(null);
        } else {
          setError(err.response?.data?.message || err.message || t('loadFailed'));
        }
      }
    } finally {
      if (!controller.signal.aborted && isMountedRef.current && currentCompanyRef.current === requestCompanyId) {
        setLoading(false);
      }
    }
  }, [currentCompany, filters, canViewPPE, t, begin]);

  const loadDropdownData = useCallback(async () => {
    if (!currentCompany?.company_id) return;

    try {
      setLoadingDropdowns(true);
      const depsRes = await departmentAPI.getDepartments(currentCompany.company_id);
      setDepartments(depsRes.departments || []);
    } catch (err) {
      console.error('Error loading dropdown data:', err);
    } finally {
      setLoadingDropdowns(false);
    }
  }, [currentCompany?.company_id]);

  useEffect(() => {
    if (currentCompany) {
      const today = getTodayDateInCompanyTimezone(currentCompany.timezone || 'UTC');
      const initialFilters = {
        date_from: today,
        date_to: today,
        per_page: 20,
        page: 1
      };
      setFilters(initialFilters);
      setFormState({
        date_from: today,
        date_to: today,
        employee_id: '',
        department_id: ''
      });
      setIsPPEDisabled(false);
      setDetections([]);
      setError(null);
    }
  }, [currentCompany]);

  useEffect(() => {
    loadDetections();
  }, [loadDetections]);

  useEffect(() => {
    loadDropdownData();
  }, [loadDropdownData]);

  const activePpeFields = useMemo(() => {
    const fields: { key: string; label: string; confKey?: string }[] = [];
    const known = {
      hairnet_gloves: [
        { key: 'hairnet', label: t('hairnet'), confKey: 'hairnet_confidence' },
        { key: 'left_glove', label: t('lGlove'), confKey: 'left_glove_confidence' },
        { key: 'right_glove', label: t('rGlove'), confKey: 'right_glove_confidence' }
      ],
      helmet: [{ key: 'helmet', label: t('helmet'), confKey: 'helmet_confidence' }],
      industrial_gloves: [{ key: 'industrial_gloves', label: t('indGloves'), confKey: 'industrial_gloves_confidence' }],
      industrial_jacket: [{ key: 'industrial_jacket', label: t('indJacket'), confKey: 'industrial_jacket_confidence' }]
    } as Record<string, Array<{ key: string; label: string; confKey?: string }>>;

    ppeTypes.forEach(type => {
      if (known[type]) {
        fields.push(...known[type]);
      } else if (type) {
        fields.push({ key: type, label: type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) });
      }
    });

    return fields;
  }, [ppeTypes, t]);

  const handleRowClick = (row: PPERow) => {
    setSelectedDetection(row);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDetection(null);
  };

  const handleImageError = useCallback((imageUrl: string) => {
    setFailedImages(prev => new Set(prev).add(imageUrl));
  }, []);

  if (userRolePermissionsLoading || loading) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-page-shell an-data-page an-ppe-page">
          <div className="page-header">
            <h2 className="an-page-title">{t('title')}</h2>
          </div>
          <div className="py-5 text-center">
            <Spinner animation="border" variant="primary" />
          </div>
        </Container>
      </Layout>
    );
  }

  if (!canViewPPE) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-page-shell an-data-page an-ppe-page">
          <div className="page-header">
            <h2 className="an-page-title">{t('title')}</h2>
          </div>
          <Alert variant="danger">{t('noAccess')}</Alert>
        </Container>
      </Layout>
    );
  }

  const handleFilterChange = (field: string, value: string) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();

    if (formState.date_from && formState.date_to && formState.date_from > formState.date_to) {
      setError(t('dateRangeError'));
      return;
    }

    setError(null);
    setFilters(prev => ({
      ...prev,
      ...formState,
      page: 1
    }));
  };

  const clearFilters = () => {
    const timezone = currentCompany?.timezone || 'UTC';
    const today = getTodayDateInCompanyTimezone(timezone);
    const clearedForm = {
      date_from: today,
      date_to: today,
      employee_id: '',
      department_id: ''
    };
    setFormState(clearedForm);
    setFilters({
      ...clearedForm,
      per_page: 20,
      page: 1
    });
    setError(null);
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const renderCompliance = (value: number | boolean | undefined, confidence?: number) => {
    if (value === undefined || value === null) return '-';
    
    const isCompliant = value === true || value === 1;
    const isNonCompliant = value === false || value === 0;

    if (!isCompliant && !isNonCompliant) return '-';

    return (
      <div className="d-flex flex-column align-items-center">
        <Badge bg={isCompliant ? 'success' : 'danger'}>
          {isCompliant ? <i className="bi bi-check-lg"></i> : <i className="bi bi-x-lg"></i>}
        </Badge>
        {confidence !== undefined && confidence !== null && (
          <small className="text-muted" style={{ fontSize: '10px' }}>
            {(confidence * 100).toFixed(0)}%
          </small>
        )}
      </div>
    );
  };

  if (isPPEDisabled) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-page-shell an-data-page an-ppe-page">
          <div className="page-header">
            <h2 className="an-page-title">{t('title')}</h2>
          </div>
          <Alert variant="info" className="mb-4">
            <Alert.Heading>{t('ppeDisabledTitle')}</Alert.Heading>
            <p>{t('ppeDisabledMessage')}</p>
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-page-shell an-data-page an-ppe-page">
        <div className="page-header">
          <h2 className="an-page-title">{t('title')}</h2>
        </div>

        <ScopeIndicator resource="ppe" />

        <Card className="mb-4 shadow-sm border-0 an-data-card an-ppe-filter-card">
          <Card.Body className="an-data-card">
            <Form onSubmit={applyFilters}>
              <Row>
                <Col md={3} className="mb-3">
                  <Form.Group>
                    <Form.Label>{tc('labels.fromDate')}</Form.Label>
                    <Form.Control 
                      type="date" 
                      value={formState.date_from || ''}
                      onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Group>
                    <Form.Label>{tc('labels.toDate')}</Form.Label>
                    <Form.Control 
                      type="date" 
                      value={formState.date_to || ''}
                      onChange={(e) => handleFilterChange('date_to', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Group>
                    <Form.Label>{tc('labels.employee')}</Form.Label>
                    <EmployeeSearchPicker companyId={currentCompany?.company_id} value={formState.employee_id}
                      onChange={employee => handleFilterChange('employee_id', employee ? String(employee.id) : '')}
                      placeholder={tc('labels.allEmployees')} />
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Group>
                    <Form.Label>{tc('labels.department')}</Form.Label>
                    <Form.Select 
                      value={formState.department_id}
                      onChange={(e) => handleFilterChange('department_id', e.target.value)}
                      disabled={loadingDropdowns}
                    >
                      <option value="">{tc('labels.allDepartments')}</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex gap-2">
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? <Spinner size="sm" /> : tc('buttons.applyFilters')}
                </Button>
                <Button variant="outline-secondary" onClick={clearFilters} disabled={loading}>
                  {tc('buttons.clear')}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}

        <Card className="shadow-sm border-0 an-data-card an-ppe-results-card">
          <Card.Body className="p-0 an-data-card">
            <div className="d-md-none p-3">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : detections.length === 0 ? (
                <div className="text-center py-5 text-muted">{t('noDetectionsFound')}</div>
              ) : (
                detections.map((row) => {
                  const companyId = currentCompany?.company_id ?? '';
                  const segments = resolvePpeImagePathSegments(row);
                  const thumbUrl =
                    companyId && segments
                      ? faceImageAPI.getImageUrl(companyId, segments.cameraSegment, segments.fileSegment)
                      : null;

                  return (
                    <PPEDetectionCard
                      key={row.id}
                      row={row}
                      thumbUrl={thumbUrl}
                      companyTimezone={companyTimezone}
                      activePpeFields={activePpeFields}
                      onOpen={handleRowClick}
                      renderCompliance={renderCompliance}
                      t={t}
                      tc={tc}
                    />
                  );
                })
              )}
            </div>

            <div className="d-none d-md-block">
              <div className="table-responsive an-data-table an-ppe-table">
                <Table hover className="align-middle mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>{t('image')}</th>
                      <th>{t('logTime')}</th>
                      <th>{tc('labels.employee')}</th>
                      <th>{tc('labels.department')}</th>
                      <th>{tc('labels.camera')}</th>
                      {activePpeFields.map(field => (
                        <th key={field.key} className="text-center">{field.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5 + activePpeFields.length} className="text-center py-5">
                          <Spinner animation="border" variant="primary" />
                        </td>
                      </tr>
                    ) : detections.length === 0 ? (
                      <tr>
                        <td colSpan={5 + activePpeFields.length} className="text-center py-5 text-muted">
                          {t('noDetectionsFound')}
                        </td>
                      </tr>
                    ) : (
                      detections.map((row) => {
                        const companyId = currentCompany?.company_id ?? '';
                        const segments = resolvePpeImagePathSegments(row);
                        const thumbUrl =
                          companyId && segments
                            ? faceImageAPI.getImageUrl(companyId, segments.cameraSegment, segments.fileSegment)
                            : null;

                        return (
                        <tr key={row.id} onClick={() => handleRowClick(row)} style={{ cursor: 'pointer' }}>
                          <td style={{ width: '80px' }}>
                            {thumbUrl ? (
                              <img 
                                src={thumbUrl}
                                alt={t('title')}
                                className="rounded shadow-sm"
                                style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                              />
                            ) : (
                              <div className="text-muted" style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--an-border)', borderRadius: 'var(--an-radius-sm)' }}>
                                {tc('messages.na')}
                              </div>
                            )}
                          </td>
                          <td>
                            <small className="d-block">{formatDateTimeInCompanyTimezone(row.log_time, companyTimezone)}</small>
                          </td>
                          <td>
                            <div className="fw-bold">{row.employee_name || t('unknown')}</div>
                            <small className="text-muted">ID: {row.emp_id || tc('messages.na')}</small>
                            <div className="mt-1 d-flex gap-1 flex-wrap">
                              {row.is_vacation && <Badge bg="info">{t('vacation')}</Badge>}
                              {row.is_holiday && <Badge bg="success">{t('holiday')}</Badge>}
                            </div>
                          </td>
                          <td>{row.department_name || t('unknown')}</td>
                          <td>{row.camera_name || t('unknown')}</td>
                          {activePpeFields.map(field => (
                            <td key={field.key} className="text-center">
                              {renderCompliance(row[field.key], field.confKey ? row[field.confKey] : undefined)}
                            </td>
                          ))}
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </div>

            {meta.last_page > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <small className="text-muted">
                  {tc('messages.showing', { from: (meta.current_page - 1) * meta.per_page + 1, to: Math.min(meta.current_page * meta.per_page, meta.total), total: meta.total, entity: '' })}
                </small>
                <Pagination className="mb-0 an-pagination">
                  <Pagination.First onClick={() => handlePageChange(1)} disabled={meta.current_page === 1} />
                  <Pagination.Prev onClick={() => handlePageChange(meta.current_page - 1)} disabled={meta.current_page === 1} />
                  {[...Array(Math.min(5, meta.last_page))].map((_, i) => {
                    let pageNum;
                    if (meta.last_page <= 5) pageNum = i + 1;
                    else if (meta.current_page <= 3) pageNum = i + 1;
                    else if (meta.current_page >= meta.last_page - 2) pageNum = meta.last_page - 4 + i;
                    else pageNum = meta.current_page - 2 + i;
                    
                    return (
                      <Pagination.Item 
                        key={pageNum} 
                        active={pageNum === meta.current_page}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}
                  <Pagination.Next onClick={() => handlePageChange(meta.current_page + 1)} disabled={meta.current_page === meta.last_page} />
                  <Pagination.Last onClick={() => handlePageChange(meta.last_page)} disabled={meta.current_page === meta.last_page} />
                </Pagination>
              </div>
            )}
          </Card.Body>
        </Card>

        <Modal 
          show={showModal} 
          onHide={handleCloseModal} 
          size="lg" 
          centered
        dialogClassName="an-ppe-modal">
          <Modal.Header closeButton className="an-section-divider">
            <Modal.Title>{t('modalTitle', { employee: selectedDetection?.employee_name })}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            {selectedDetection && (() => {
              const companyId = currentCompany?.company_id ?? '';
              const segments = resolvePpeImagePathSegments(selectedDetection);
              const imageUrl =
                companyId && segments
                  ? faceImageAPI.getImageUrl(companyId, segments.cameraSegment, segments.fileSegment)
                  : null;
              const isModalImageFailed = imageUrl ? failedImages.has(imageUrl) : false;

              return (
                <div className="image-container">
                  <div className="position-relative w-100">
                    <div className="d-flex justify-content-center w-100">
                      {imageUrl && !isModalImageFailed && selectedDetection.coordinates ? (
                        <ImageWithDetectionBox
                          src={imageUrl}
                          coordinates={selectedDetection.coordinates}
                          alt={`${selectedDetection.employee_name} detection`}
                          className="img-fluid w-100"
                          style={{ maxHeight: '70vh', width: '100%' }}
                          showDetectionBox={false}
                          showEmployeeName={true}
                          employeeName={selectedDetection.employee_name}
                          onError={() => handleImageError(imageUrl)}
                        />
                      ) : (
                        <div className="text-center p-5">
                          <i className="bi bi-image mb-3" style={{ fontSize: '3rem', color: 'var(--an-border)' }}></i>
                          <p className="text-muted">{tc('messages.na')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="p-3 an-modal-detail-strip reports-modal-details">
                    <Row>
                      <Col md={6}>
                        <div className="mb-2">
                          <strong>{tc('labels.employee')}</strong> {selectedDetection.employee_name || t('unknown')} ({selectedDetection.emp_id || tc('messages.na')})
                          <div className="mt-1 d-flex gap-1 flex-wrap">
                            {selectedDetection.is_vacation && <Badge bg="info">{t('vacation')}</Badge>}
                            {selectedDetection.is_holiday && <Badge bg="success">{t('holiday')}</Badge>}
                          </div>
                        </div>
                        <div className="mb-2">
                          <strong>{tc('labels.department')}</strong> {selectedDetection.department_name || t('unknown')}
                        </div>
                        <div className="mb-2">
                          <strong>{tc('labels.camera')}</strong> {selectedDetection.camera_name || t('unknown')} ({selectedDetection.camera_number || tc('messages.na')})
                        </div>
                        {selectedDetection.coordinates && (
                          <div className="mb-2">
                            <strong>{t('modalCoordinates')}</strong>
                            <br />
                            <span className="coordinates-text">
                              ({selectedDetection.coordinates.x1}, {selectedDetection.coordinates.y1}) {'->'} ({selectedDetection.coordinates.x2}, {selectedDetection.coordinates.y2})
                            </span>
                          </div>
                        )}
                      </Col>
                      <Col md={6}>
                        <div className="mb-2">
                          <strong>{t('time')}</strong> {formatDateTimeInCompanyTimezone(selectedDetection.log_time, companyTimezone)}
                        </div>
                        {selectedDetection.model_setup && (
                          <div className="mb-3">
                            <strong>{t('modalModelSetup')}</strong> {selectedDetection.model_setup}
                          </div>
                        )}

                        <h6 className="fw-bold mb-2">{t('ppeCompliance')}</h6>
                        <div className="d-flex flex-wrap gap-2">
                          {activePpeFields.map(field => (
                            <div key={field.key} className="d-flex flex-column align-items-center p-2 an-panel-soft" style={{ minWidth: '70px' }}>
                              <small className="text-muted mb-1 text-center" style={{ fontSize: '0.65rem' }}>{field.label}</small>
                              {renderCompliance(selectedDetection[field.key], field.confKey ? selectedDetection[field.confKey] : undefined)}
                            </div>
                          ))}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              );
            })()}
          </Modal.Body>
        </Modal>
      </Container>
    </Layout>
  );
};

export default PPEDetections;



